import React, { useState, useMemo } from 'react';
import { type NodeRendererProps, Tree } from 'react-arborist';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import View from '../../ui/View';
import Text from '../../ui/Text';
import { groupBy, get } from 'lodash';
import { type PnlItem } from '../pnl';
import { formatCurrency } from '../../../lib/format';

// Node types for the tree
type CostNodeType = 'group' | 'cost' | 'sum';
type GroupingField =
  | 'category'
  | 'shipmentId'
  | 'portCallId'
  | 'type'
  | 'timelineName'
  | 'timelineType';
type SortOrder = 'asc' | 'desc' | 'none';

interface CostNode {
  id: string;
  name: string;
  type: CostNodeType;
  cost?: number;
  children: CostNode[] | null;
  data: {
    originalItems: PnlItem[];
    // For individual cost items, we still keep a direct reference for convenience
    item?: PnlItem;
  };
}

interface PnlDebugViewProps {
  pnlItems: PnlItem[]; // Array of PnL items to display in the tree view
  initialGrouping?: GroupingField[]; // Optional initial grouping fields
}

// Simple QuestionMark icon for tooltips
const QuestionMarkIcon = ({
  size = 16,
  color = '#999',
}: {
  size?: number;
  color?: string;
}) => (
  <div
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: `${size}px`,
      height: `${size}px`,
      borderRadius: '50%',
      border: `1px solid ${color}`,
      color: color,
      fontSize: `${size * 0.7}px`,
      fontWeight: 'bold',
      cursor: 'help',
    }}
  >
    ?
  </div>
);

const GroupingOption = styled.button<{ isActive: boolean }>`
  background-color: ${(props) =>
    props.isActive
      ? themeGet('colors.primary.light')
      : themeGet('colors.neutral.gray_10')};
  border: 1px solid
    ${(props) =>
      props.isActive
        ? themeGet('colors.primary')
        : themeGet('colors.neutral.gray_100')};
  padding: ${themeGet('space.2')}px ${themeGet('space.3')}px;
  margin-right: ${themeGet('space.2')}px;
  margin-bottom: ${themeGet('space.2')}px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: ${(props) => (props.isActive ? 'bold' : 'normal')};
  color: ${(props) =>
    props.isActive ? themeGet('colors.primary') : themeGet('colors.text')};

  &:hover {
    background-color: ${(props) =>
      props.isActive
        ? themeGet('colors.primary.lighter')
        : themeGet('colors.neutral.gray_30')};
  }
`;

const OrderButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${themeGet('colors.primary')};
  padding: 0 2px;
  font-size: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    color: ${themeGet('colors.primary.dark')};
    background-color: ${themeGet('colors.neutral.gray_30')};
  }
`;

const FilterControls = styled(View)`
  flex-direction: column;
  margin-bottom: ${themeGet('space.4')}px;
`;

const ActiveFilters = styled(View)`
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  margin-top: ${themeGet('space.3')}px;
  margin-bottom: ${themeGet('space.3')}px;
  min-height: 48px;
  border: 1px dashed ${themeGet('colors.neutral.gray_100')};
  padding: ${themeGet('space.2')}px;
  border-radius: 4px;
  background-color: ${themeGet('colors.neutral.gray_10')};
`;

const InactiveFilters = styled(View)`
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  margin-top: ${themeGet('space.2')}px;
`;

const ActiveFilterItem = styled(View)`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-right: ${themeGet('space.3')}px;
  position: relative;

  &:hover {
    & > div > button {
      background-color: ${themeGet('colors.neutral.gray_20')};
    }

    & > button {
      background-color: ${themeGet('colors.primary.lighter')};
    }
  }
`;

const HeaderControls = styled(View)`
  flex-direction: row;
  align-items: flex-start;
  margin-bottom: ${themeGet('space.3')}px;
  justify-content: space-between;
`;

const ControlGroup = styled(View)`
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
`;

const NodeContainer = styled(View)<{ depth: number; isSelected: boolean }>`
  padding: ${themeGet('space.2')}px ${themeGet('space.3')}px;
  margin-left: ${(props) => props.depth * 20}px;
  border-left: ${(props) =>
    props.isSelected
      ? '3px solid ' + themeGet('colors.primary')(props)
      : 'none'};
  cursor: pointer;
  background-color: ${(props) =>
    props.isSelected
      ? themeGet('colors.neutral.gray_10')(props)
      : 'transparent'};

  &:hover {
    background-color: ${themeGet('colors.neutral.gray_10')};
  }
`;

const NodeContent = styled(View)`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const MainContainer = styled(View)`
  display: flex;
  width: 100%;
  gap: 16px;
  flex-direction: row;

  @media (max-width: 1024px) {
    flex-direction: column;
  }
`;

const TreeContainer = styled(View)`
  border: 1px solid ${themeGet('colors.neutral.gray_50')};
  border-radius: 4px;
  overflow: hidden;
  height: 500px;
  flex: 1;
`;

const DetailsContainer = styled(View)`
  border: 1px solid ${themeGet('colors.neutral.gray_50')};
  border-radius: 4px;
  padding: 16px;
  width: 300px;
  height: 500px;
  overflow-y: auto;
`;

const DetailsRow = styled(View)`
  flex-direction: row;
  justify-content: space-between;
  border-bottom: 1px solid ${themeGet('colors.neutral.gray_30')};
  padding: 8px 0;
`;

const SortButton = styled.button<{ sortOrder: SortOrder }>`
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${(props) => {
    if (props.sortOrder === 'none')
      return themeGet('colors.neutral.gray_400')(props);
    return themeGet('colors.primary')(props);
  }};
  margin-left: 8px;
  font-size: 16px;
`;

const Tooltip = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  background-color: ${themeGet('colors.neutral.black_600')};
  color: white;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 12px;
  z-index: 100;
  width: 250px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  display: none;
`;

const TooltipContainer = styled.div`
  position: relative;
  display: inline-block;
  margin-left: 8px;

  &:hover ${Tooltip} {
    display: block;
  }
`;

const HelpText = ({ text }: { text: string }) => (
  <TooltipContainer>
    <QuestionMarkIcon />
    <Tooltip>{text}</Tooltip>
  </TooltipContainer>
);

// Node component used by react-arborist
function Node({ node, style, dragHandle }: NodeRendererProps<CostNode>) {
  const data = node.data;

  return (
    <NodeContainer
      depth={node.level}
      isSelected={node.isSelected}
      style={style}
      ref={dragHandle}
      onClick={() => (node.isInternal ? node.toggle() : node.select())}
    >
      <NodeContent>
        <View flexDirection="row" alignItems="center">
          {!node.isLeaf && (
            <Text mr={1} fontSize={1}>
              {node.isOpen ? '▼' : '▶'}
            </Text>
          )}
          {node.isLeaf ? (
            <Text fontSize={1}>{data.name}</Text>
          ) : (
            <Text fontWeight="bold" fontSize={1}>
              {data.name}
            </Text>
          )}
        </View>
        {data.cost !== undefined && (
          <Text
            fontSize={1}
            fontWeight={data.type === 'sum' ? 'bold' : 'normal'}
          >
            {/* TODO get org default currency */}
            {formatCurrency(data.cost, 0, undefined)}
          </Text>
        )}
      </NodeContent>
    </NodeContainer>
  );
}

export default function PnlDebugView({
  initialGrouping,
  pnlItems,
}: PnlDebugViewProps) {
  const [groupingFields, setGroupingFields] = useState<GroupingField[]>(
    initialGrouping || ['category'],
  );
  const [showIndividualCosts, setShowIndividualCosts] = useState(true);
  const [selectedNode, setSelectedNode] = useState<CostNode | null>(null);
  const [sortField, setSortField] = useState<string>('cost');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  // Toggle a grouping field in the list
  const toggleGroupingField = (field: GroupingField) => {
    if (groupingFields.includes(field)) {
      setGroupingFields(groupingFields.filter((f) => f !== field));
    } else {
      setGroupingFields([...groupingFields, field]);
    }

    // Reset selected node when grouping changes
    setSelectedNode(null);
  };

  // Move a grouping field up or down in priority
  const moveGroupingField = (
    field: GroupingField,
    direction: 'up' | 'down',
  ) => {
    const index = groupingFields.indexOf(field);
    if (index === -1) return;

    const newGroupingFields = [...groupingFields];
    const newIndex = direction === 'up' ? index - 1 : index + 1;

    if (newIndex < 0 || newIndex >= groupingFields.length) return;

    [newGroupingFields[index], newGroupingFields[newIndex]] = [
      newGroupingFields[newIndex],
      newGroupingFields[index],
    ];

    setGroupingFields(newGroupingFields);
  };

  // Handle sort toggle
  const toggleSort = (field: string) => {
    if (sortField === field) {
      // Cycle through sort orders: asc -> desc -> none
      if (sortOrder === 'asc') {
        setSortOrder('desc');
      } else if (sortOrder === 'desc') {
        setSortOrder('none');
      } else {
        setSortOrder('asc');
      }
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // Build tree data based on current grouping fields
  // Transform PnlItems into tree structure based on grouping fields
  const treeData = useMemo(() => {
    if (!pnlItems || pnlItems.length === 0) {
      return [];
    }

    // If no grouping fields are selected, show all individual costs
    if (groupingFields.length === 0) {
      return pnlItems.map((item, index) => ({
        id: `cost-${index}`,
        name: `${item.type === 'cost' ? 'Cost' : 'Income'}: ${item.category || 'Unknown'}`,
        type: 'cost' as CostNodeType,
        cost: item.amount,
        data: {
          originalItems: [item],
          item: item,
        },
        children: null,
      }));
    }

    // Function to recursively group costs
    const groupCosts = (
      items: PnlItem[],
      fields: GroupingField[],
      currentPath: string = '',
      depth: number = 0,
    ): CostNode[] => {
      if (fields.length === 0) {
        if (showIndividualCosts) {
          // Return individual cost items
          return items.map((item, index) => ({
            id: `${currentPath}-cost-${index}`,
            name: `${item.type === 'cost' ? 'Cost' : 'Income'}: ${item.category || 'Unknown'}`,
            type: 'cost' as CostNodeType,
            cost: item.amount,
            data: {
              originalItems: [item],
              item: item,
            },
            children: null,
          }));
        } else {
          // Return a single sum node
          const totalCost = items.reduce((sum, item) => sum + item.amount, 0);
          return [
            {
              id: `${currentPath}-sum`,
              name: `Total`,
              type: 'sum' as CostNodeType,
              cost: totalCost,
              data: {
                originalItems: items,
              },
              children: null,
            },
          ];
        }
      }

      const currentField = fields[0];
      const restFields = fields.slice(1);

      // Group items by the current field
      const grouped = groupBy(items, (item) => {
        const value = get(item, currentField);
        return value !== null && value !== undefined ? value : 'Unspecified';
      });

      // Create nodes for each group
      let result = Object.entries(grouped).map(([key, groupItems]) => {
        const groupId = `${currentPath}-${currentField}-${key}`;
        const groupName = `${currentField}: ${key}`;
        const totalCost = groupItems.reduce(
          (sum, item) => sum + item.amount,
          0,
        );

        return {
          id: groupId,
          name: groupName,
          type: 'group' as CostNodeType,
          cost: totalCost,
          children: groupCosts(groupItems, restFields, groupId, depth + 1),
          data: {
            originalItems: groupItems,
          },
        };
      });

      // Sort nodes if sorting is enabled
      if (sortOrder !== 'none') {
        result = result.sort((a, b) => {
          let compareValue;

          if (sortField === 'cost') {
            compareValue = a.cost - b.cost;
          } else if (sortField === 'name') {
            compareValue = a.name.localeCompare(b.name);
          } else {
            return 0;
          }

          return sortOrder === 'asc' ? compareValue : -compareValue;
        });
      }

      return result;
    };

    return groupCosts(pnlItems, groupingFields);
  }, [pnlItems, groupingFields, showIndividualCosts, sortField, sortOrder]);

  const availableGroupingFields: { field: GroupingField; label: string }[] = [
    { field: 'category', label: 'Category' },
    { field: 'timelineName', label: 'Timeline' },
    { field: 'timelineType', label: 'Timeline Type' },
    { field: 'shipmentId', label: 'Shipment' },
    { field: 'portCallId', label: 'Port Call' },
    { field: 'type', label: 'Type' },
  ];

  return (
    <View width="100%" height="100%">
      <HeaderControls>
        <FilterControls>
          <View flexDirection="row" alignItems="center">
            <Text mr={2} fontWeight="bold">
              Group by:
            </Text>
            <HelpText text="Select properties to group costs by. Arrange active filters with arrows to change their priority." />
          </View>

          <Text fontSize={1} fontWeight="medium" mb={1}>
            Available filters (click to add):
          </Text>
          <InactiveFilters>
            {availableGroupingFields
              .filter(({ field }) => !groupingFields.includes(field))
              .map(({ field, label }) => (
                <GroupingOption
                  key={field}
                  isActive={false}
                  onClick={() => toggleGroupingField(field)}
                >
                  {label}
                </GroupingOption>
              ))}
          </InactiveFilters>

          <Text fontSize={1} color="neutral.gray_400" mt={3} mb={1}>
            {groupingFields.length === 0
              ? '(No grouping - showing all costs)'
              : 'Active filters (ordered by priority):'}
          </Text>

          <ActiveFilters>
            {groupingFields.length === 0 && (
              <Text
                color="neutral.gray_400"
                fontSize={1}
                p={2}
                textAlign="center"
                width="100%"
              >
                No active filters. Select filters from above to create a
                grouping hierarchy.
              </Text>
            )}
            {groupingFields.map((field, index) => {
              const label =
                availableGroupingFields.find((f) => f.field === field)?.label ||
                field;
              return (
                <ActiveFilterItem key={field}>
                  <GroupingOption
                    isActive={true}
                    onClick={() => toggleGroupingField(field)}
                  >
                    #{index + 1}: {label}
                  </GroupingOption>
                  <View>
                    {index > 0 && (
                      <OrderButton
                        onClick={() => moveGroupingField(field, 'up')}
                        title="Move left (higher priority)"
                      >
                        ←
                      </OrderButton>
                    )}
                    {index < groupingFields.length - 1 && (
                      <OrderButton
                        onClick={() => moveGroupingField(field, 'down')}
                        title="Move right (lower priority)"
                      >
                        →
                      </OrderButton>
                    )}
                  </View>
                </ActiveFilterItem>
              );
            })}
          </ActiveFilters>
        </FilterControls>

        <View>
          <ControlGroup>
            <Text mr={2} fontWeight="bold">
              Show:
            </Text>
            <HelpText text="'Individual Costs' shows all cost items. 'Sums Only' shows aggregated costs at each level." />
            <GroupingOption
              isActive={showIndividualCosts}
              onClick={() => setShowIndividualCosts(true)}
            >
              Individual Costs
            </GroupingOption>
            <GroupingOption
              isActive={!showIndividualCosts}
              onClick={() => setShowIndividualCosts(false)}
            >
              Sums Only
            </GroupingOption>
          </ControlGroup>
        </View>
      </HeaderControls>

      <ControlGroup mb={3} mt={3}>
        <Text mr={2} fontWeight="bold">
          Sort by:
        </Text>
        <HelpText text="Sort costs within each group level. Click multiple times to cycle through ascending, descending, and no sort." />
        <ControlGroup>
          <Text>Cost</Text>
          <SortButton
            sortOrder={sortField === 'cost' ? sortOrder : 'none'}
            onClick={() => toggleSort('cost')}
          >
            {sortField === 'cost'
              ? sortOrder === 'asc'
                ? '↑'
                : sortOrder === 'desc'
                  ? '↓'
                  : '•'
              : '•'}
          </SortButton>
        </ControlGroup>
        <ControlGroup ml={3}>
          <Text>Name</Text>
          <SortButton
            sortOrder={sortField === 'name' ? sortOrder : 'none'}
            onClick={() => toggleSort('name')}
          >
            {sortField === 'name'
              ? sortOrder === 'asc'
                ? '↑'
                : sortOrder === 'desc'
                  ? '↓'
                  : '•'
              : '•'}
          </SortButton>
        </ControlGroup>
      </ControlGroup>

      <MainContainer>
        <TreeContainer>
          {treeData.length > 0 ? (
            <Tree
              childrenAccessor={(node) => node.children}
              data={treeData}
              disableDrag
              disableDrop
              disableEdit
              height={500}
              idAccessor={(node) => node.id}
              indent={24}
              onSelect={(nodes) => {
                try {
                  const node = Array.isArray(nodes) ? nodes.at(0) : nodes;
                  if (node) {
                    setSelectedNode(node.data);
                  } else {
                    setSelectedNode(null);
                  }
                } catch (e) {
                  console.error('Error selecting node:', e);
                  setSelectedNode(null);
                }
              }}
              openByDefault={false}
              rowHeight={32}
              selection="single"
              width="100%"
            >
              {Node}
            </Tree>
          ) : (
            <View
              p={4}
              alignItems="center"
              justifyContent="center"
              height="100%"
            >
              <Text color="neutral.gray_400">
                No cost data available. Make sure PnL is enabled and costs are
                being calculated.
              </Text>
            </View>
          )}
        </TreeContainer>

        {selectedNode && (
          <DetailsContainer>
            <Text fontWeight="bold" fontSize={2} mb={3}>
              Cost Details
              <HelpText text="Select a node in the tree to view detailed information about that cost or group." />
            </Text>

            {selectedNode.type === 'cost' && (
              <>
                {selectedNode.data.item &&
                  Object.entries(selectedNode.data.item)
                    .filter(
                      ([key, value]) => key !== 'id' && value !== undefined,
                    )
                    .map(([key, value]) => (
                      <DetailsRow key={key}>
                        <Text fontWeight="bold">{key}:</Text>
                        <Text>
                          {key === 'amount'
                            ? /* TODO: Use org default currency */
                              formatCurrency(value as number, 2, undefined)
                            : typeof value === 'number'
                              ? Number(value).toLocaleString()
                              : typeof value === 'boolean'
                                ? value
                                  ? 'Yes'
                                  : 'No'
                                : value?.toString() || 'N/A'}
                        </Text>
                      </DetailsRow>
                    ))}
              </>
            )}

            {selectedNode.type === 'sum' && (
              <>
                <DetailsRow>
                  <Text fontWeight="bold">Total Amount:</Text>
                  <Text fontWeight="bold">
                    {/* TODO: Use org default currency */}
                    {formatCurrency(selectedNode.cost || 0, 0, undefined)}
                  </Text>
                </DetailsRow>
                <DetailsRow>
                  <Text fontWeight="bold">Items:</Text>
                  <Text>{selectedNode.data.originalItems?.length || 0}</Text>
                </DetailsRow>
                <DetailsRow>
                  <Text fontWeight="bold">Item Types:</Text>
                  <Text>
                    Cost:{' '}
                    {selectedNode.data.originalItems?.filter(
                      (item) => item.type === 'cost',
                    ).length || 0}
                    , Income:{' '}
                    {/*
                      TODO: Income not implemented yet
                      selectedNode.data.originalItems?.filter(
                      (item) => item.type === 'income',
                    ).length || 0
                    */}
                  </Text>
                </DetailsRow>
              </>
            )}

            {selectedNode.type === 'group' && (
              <>
                <DetailsRow>
                  <Text fontWeight="bold">Group:</Text>
                  <Text>{selectedNode.name}</Text>
                </DetailsRow>
                <DetailsRow>
                  <Text fontWeight="bold">Total Amount:</Text>
                  <Text fontWeight="bold">
                    {/* TODO: Use org default currency */}
                    {formatCurrency(selectedNode.cost || 0, 0, undefined)}
                  </Text>
                </DetailsRow>
                <DetailsRow>
                  <Text fontWeight="bold">Child Nodes:</Text>
                  <Text>{selectedNode.children?.length || 0}</Text>
                </DetailsRow>
                <DetailsRow>
                  <Text fontWeight="bold">Original Items:</Text>
                  <Text>{selectedNode.data.originalItems?.length || 0}</Text>
                </DetailsRow>
                <DetailsRow>
                  <Text fontWeight="bold">Item Types:</Text>
                  <Text>
                    Cost:{' '}
                    {selectedNode.data.originalItems?.filter(
                      (item) => item.type === 'cost',
                    ).length || 0}
                    , Income:{' '}
                    {/*
                      TODO: Income not implemented yet
                      selectedNode.data.originalItems?.filter(
                      (item) => item.type === 'income',
                    ).length || 0
                    */}
                  </Text>
                </DetailsRow>
              </>
            )}
          </DetailsContainer>
        )}
      </MainContainer>
    </View>
  );
}
